import React from "react";
import {
  Alert,
  Box,
  Divider,
  FormControlLabel,
  FormLabel,
  Radio,
  RadioGroup,
  Typography,
} from "@mui/material";
import HPDropdown from "@components/common/Dropdown";
import RollupBuilder from "@components/modals/chart-modal/RollupBuilder";
import HiddenInDatasetBadge from "@components/common/HiddenInDatasetBadge";
import { useFocusOptionList } from "@hooks/use-focus-option-list";
import { t } from "@utils/i18n";
import type { QuestionVariable, QuestionValue } from "@models/Filter";

interface FocusModeSectionProps {
  focusLevel: "variable" | "value";
  allVariables: QuestionVariable[];
  allValues: QuestionValue[];
  isDisabled: boolean;
  focusMetric: "single" | "rollup";
  selectedVariableId: string;
  selectedValueId: string;
  rollupName: string;
  rollupSelectedLabels: string[];
  onFocusLevelChange: (level: "variable" | "value") => void;
  onFocusParamsChange: (params: {
    focusMode: string;
    focusEntity: string;
    focusEntityDisplayLabel?: string;
  } | null) => void;
  onFocusMetricChange: (metric: "single" | "rollup") => void;
  onRollupNameChange: (name: string) => void;
  onRollupLabelsChange: (labels: string[]) => void;
  /** Word used in the level-selection labels — "bars" for bar charts, "lines" for line charts. */
  displayUnit?: "bars" | "lines";
}

const FocusModeSection: React.FC<FocusModeSectionProps> = ({
  focusLevel,
  allVariables,
  allValues,
  isDisabled,
  focusMetric,
  selectedVariableId,
  selectedValueId,
  rollupName,
  rollupSelectedLabels,
  onFocusLevelChange,
  onFocusParamsChange,
  onFocusMetricChange,
  onRollupNameChange,
  onRollupLabelsChange,
  displayUnit = "bars",
}) => {
  const activeCategories = focusLevel === "value" ? allValues : allVariables;
  const showMetricToggle = activeCategories.length >= 2;

  const { visibleItems: variableOptions, isSelectedHidden: isVariableHidden } =
    useFocusOptionList(allVariables, selectedVariableId);
  const { visibleItems: valueOptions, isSelectedHidden: isValueHidden } =
    useFocusOptionList(allValues, selectedValueId);

  // Zero visible sources in the focused dimension — neither a single focus nor
  // a new rollup can be built; explain the empty state rather than show a blank
  // picker. A saved hidden focus still renders (tagged) via the options above.
  const activeVisibleCount =
    focusLevel === "value"
      ? allValues.filter((v) => !v.hidden).length
      : allVariables.filter((v) => !v.hidden).length;
  const emptyStateText =
    focusLevel === "value"
      ? "All values for this focus are hidden in the dataset. Restore content in Questions to choose a value."
      : "All variables for this focus are hidden in the dataset. Restore content in Questions to choose a variable.";
  const noOptionsText =
    focusLevel === "value"
      ? "No visible values — restore content in Questions."
      : "No visible variables — restore content in Questions.";

  return (
    <>
      <Box
        sx={{
          p: 2,
          border: "1px solid #e0e0e0",
          borderRadius: 1,
        }}
      >
        <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
          Focus Selection
        </Typography>
        <Typography variant="body2" sx={{ mb: 2, color: "text.secondary" }}>
          This question has multiple variables. Choose how to display the data:
        </Typography>

        <RadioGroup
          value={focusLevel}
          onChange={(e) => onFocusLevelChange(e.target.value as "variable" | "value")}
          sx={{ mb: 1 }}
        >
          <FormControlLabel
            value="variable"
            control={<Radio size="small" />}
            label={`Focus on a specific variable (show all its values as ${displayUnit})`}
            disabled={isDisabled}
          />
          <FormControlLabel
            value="value"
            control={<Radio size="small" />}
            label={`Focus on a specific value (show it across all variables as ${displayUnit})`}
            disabled={isDisabled}
          />
        </RadioGroup>

        {activeVisibleCount === 0 && (
          <Alert severity="warning" sx={{ mt: 1, fontSize: "0.8125rem" }}>
            {emptyStateText}
          </Alert>
        )}

        {showMetricToggle && (
          <Box sx={{ mt: 1 }}>
            <FormLabel sx={{ fontSize: 12, color: "#262626", mb: 0.5, display: "block" }}>
              Response Selection
            </FormLabel>
            <RadioGroup
              value={focusMetric}
              onChange={(e) => onFocusMetricChange(e.target.value as "single" | "rollup")}
            >
              <FormControlLabel
                value="single"
                control={<Radio size="small" />}
                label="Single Category"
                disabled={isDisabled}
              />
              <FormControlLabel
                value="rollup"
                control={<Radio size="small" />}
                label="Rollup"
                disabled={isDisabled}
              />
            </RadioGroup>
          </Box>
        )}

        {focusMetric === "rollup" ? (
          <Box sx={{ mt: 2 }}>
            <RollupBuilder
              focusMode={focusLevel}
              allVariables={allVariables}
              allValues={allValues}
              rollupName={rollupName}
              rollupSelectedLabels={rollupSelectedLabels}
              onRollupNameChange={onRollupNameChange}
              onSelectedLabelsChange={onRollupLabelsChange}
            />
          </Box>
        ) : (
          <>
            {focusLevel === "variable" && (
              <Box sx={{ mt: 2 }}>
                <HPDropdown
                  label="Select Variable*"
                  value={selectedVariableId}
                  onChange={(value) => {
                    const variable = allVariables.find(
                      (v) => v.datasetElementId === value,
                    );
                    if (variable) {
                      onFocusParamsChange({
                        focusMode: "VARIABLE",
                        focusEntity: variable.label,
                        focusEntityDisplayLabel: variable.rowtitle || variable.title,
                      });
                    }
                  }}
                  placeholder="Choose a variable"
                  options={variableOptions.map((v) => ({
                    value: v.datasetElementId,
                    label: v.rowtitle || v.title,
                    subtitle: v.hidden ? t("common.hiddenInDataset") : undefined,
                  }))}
                  getOptionDisabled={(option) =>
                    variableOptions.find((v) => v.datasetElementId === option.value)
                      ?.hidden === true && option.value !== selectedVariableId
                  }
                  noOptionsText={noOptionsText}
                  disabled={isDisabled}
                />
                {isVariableHidden && (
                  <Box sx={{ mt: 0.5 }}>
                    <HiddenInDatasetBadge />
                  </Box>
                )}
              </Box>
            )}

            {focusLevel === "value" && (
              <Box sx={{ mt: 2 }}>
                <HPDropdown
                  label="Select Value*"
                  value={selectedValueId}
                  onChange={(value) => {
                    const valueObj = allValues.find(
                      (v) => v.datasetElementId === value,
                    );
                    if (valueObj) {
                      onFocusParamsChange({
                        focusMode: "VALUE",
                        focusEntity: String(valueObj.value),
                        focusEntityDisplayLabel:
                          valueObj.rowtitle || valueObj.title || valueObj.label,
                      });
                    }
                  }}
                  placeholder="Choose a value"
                  options={valueOptions.map((v) => ({
                    value: v.datasetElementId,
                    label: v.title || v.label,
                    subtitle: v.hidden ? t("common.hiddenInDataset") : undefined,
                  }))}
                  getOptionDisabled={(option) =>
                    valueOptions.find((v) => v.datasetElementId === option.value)
                      ?.hidden === true && option.value !== selectedValueId
                  }
                  noOptionsText={noOptionsText}
                  disabled={isDisabled}
                />
                {isValueHidden && (
                  <Box sx={{ mt: 0.5 }}>
                    <HiddenInDatasetBadge />
                  </Box>
                )}
              </Box>
            )}
          </>
        )}
      </Box>

      <Divider />
    </>
  );
};

export default FocusModeSection;
